import React from 'react'
import Joi from 'joi'
import { t } from 'i18next'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, Campaign as CampaignIcon, PlayArrowRounded, OpenInNew, ContentCopy, ScienceOutlined } from '@mui/icons-material'
import { useDispatch, useSelector } from '../../../store'
import { activateCampaign, postCampaign, putCampaign } from '../../../store/marketing/actionCreator'
import { getPackageRules } from '../../../store/package/actionCreator'
import { clearFieldError } from '../../../store/common/actionCreator'
import { ErrorsState, FormState, select, validateForm } from '../../../function/validation'
import { CampaignFrequency, CampaignType, ICampaign, SubscriberType } from '../../../store/marketing/reducer'
import { TableRef } from '../../../component/datatable/types'
import { api } from '../../../function/axios'
import { Package } from '../../../store/package/reducer'
import { Billing, buyActionUrl, parseBuyAction } from './campaignAction'
import { checkoutUrl, readableUrl } from './checkoutPreview'
import { CampaignScreen } from './CampaignPreviewV2'
import { ParagraphEditor } from './ParagraphEditor'
import { PackagePicker } from './PackagePicker'
import {
  joinVideo, paragraphStats, paragraphText, PARAGRAPH_MAX_CODES, PARAGRAPH_MAX_LINKS, PARAGRAPH_MAX_LIST_ITEMS, splitVideo, youtubeId, youtubeThumb,
} from './paragraphFormat'
import l from '../../../page/SubscribersPageV2.module.css'
import s from './CampaignDialogV2.module.css'

/*
 * Create / edit a pop-up campaign — marketing_v2's dialog. The classic form
 * (component/marketing/FormCampaign.tsx, behind CampaignCreate and
 * PopupCampaign) is untouched and still serves /marketing; this one keeps its
 * fields, its rules (free package → no subscriber tiers and no LTV; ranges
 * "to" above "from"; at least one page, package and tier) and its request
 * body, laid out as sections with the pop-up as the user will see it on the
 * right (CampaignPopup — the new dashboard's own), updating as you type.
 *
 * An optional YouTube video above the text is kept in the paragraph's first
 * line (paragraphFormat.ts), so it needs no backend field either.
 *
 * The button either opens a link or buys a package (campaignAction.ts —
 * still just `actionUrl`, the dashboard builds the user's checkout).
 *
 * Changes: the text limits and the link check the classic form has
 * commented out are on — the same ones its Telegram form enforces — and the
 * paragraph is a small editor — styles, a list, links, a promo code
 * (ParagraphEditor; stored as text with markup, see paragraphFormat.ts).
 */

const NAME_MAX = 55
const TITLE_MAX = 255
const PARAGRAPH_MAX = 500
const PARAGRAPH_STORED_MAX = 2000
const ACTION_MAX = 25

const TYPES: { key: CampaignType; label: string }[] = [
  { key: 'bottom-right', label: 'Bottom-right corner' },
  { key: 'center-overlay', label: 'Centre overlay' },
]
const FREQUENCIES: { key: CampaignFrequency; label: string }[] = [
  { key: 'once-a-day', label: 'Once a day' },
  { key: 'every-login', label: 'Every login' },
]
const TIERS: { key: SubscriberType; label: string }[] = [
  { key: 'gold', label: String(t('subscriber.type.gold')) },
  { key: 'silver', label: String(t('subscriber.type.silver')) },
  { key: 'bronze', label: String(t('subscriber.type.bronze')) },
]
const PAGES: { key: string; label: string }[] = [
  { key: '/', label: 'Dashboard' }, { key: '/search', label: 'Search' }, { key: '/packages', label: 'Packages' },
  { key: '/developer', label: 'Developer' }, { key: '/apikey', label: 'API key' }, { key: '/support', label: 'Support' },
]

const schema = Joi.object({
  name: Joi.string().max(NAME_MAX).label('Campaign name'),
  title: Joi.string().max(TITLE_MAX).label('Main title'),
  // the stored paragraph carries its links' addresses; the limit is on what users read
  paragraph: Joi.string().max(PARAGRAPH_STORED_MAX).label('Paragraph'),
  paragraphText: Joi.string().max(PARAGRAPH_MAX).label('Paragraph'),
  videoOk: Joi.boolean().invalid(false).messages({ 'any.invalid': 'Paste a YouTube link — youtube.com/watch?v=… or youtu.be/…' }),
  paragraphLinks: Joi.number().max(PARAGRAPH_MAX_LINKS).messages({ 'number.max': `Up to ${PARAGRAPH_MAX_LINKS} links in the paragraph` }),
  paragraphCodes: Joi.number().max(PARAGRAPH_MAX_CODES).messages({ 'number.max': 'One promo code in the paragraph' }),
  paragraphListItems: Joi.number().max(PARAGRAPH_MAX_LIST_ITEMS).messages({ 'number.max': `Up to ${PARAGRAPH_MAX_LIST_ITEMS} items in a list` }),
  actionName: Joi.string().max(ACTION_MAX).label('Button text'),
  actionKind: Joi.allow(),
  actionUrl: Joi.any().when('actionKind', {
    is: 'link', then: Joi.string().uri({ allowQuerySquareBrackets: true }).label('Button link'), otherwise: Joi.allow(),
  }),
  buyPackage: Joi.any().when('actionKind', {
    is: 'package',
    then: Joi.number().required().messages({ 'any.required': 'Choose the package to buy', 'number.base': 'Choose the package to buy' }),
    otherwise: Joi.allow(),
  }),
  type: select.label('Pop-up type'),
  durationHour: Joi.number().integer().min(0).max(999).label('Hours'),
  durationMinute: Joi.number().integer().min(0).max(59).label('Minutes'),
  showCountdown: Joi.boolean(),
  frequency: select.label('Frequency'),
  isOnlyFreePackageSelected: Joi.allow(),
  isLtv: Joi.allow(),
  isBalanceRange: Joi.allow(),
  isLastActive: Joi.allow(),
  subscribers: Joi.any().label('Subscribers').when('isOnlyFreePackageSelected', {
    is: true, then: Joi.any(), otherwise: Joi.array().required().min(1),
  }),
  pages: Joi.array().required().min(1).label('Pages'),
  packages: Joi.array().required().min(1).label('Packages'),
  ltvFrom: Joi.any().label('From').when('isLtv', { is: true, then: Joi.number().required().min(0).max(999999), otherwise: Joi.allow() }),
  ltvTo: Joi.any().label('To').when('isLtv', {
    is: true, then: Joi.number().required().min(1).max(999999).greater(Joi.ref('ltvFrom')), otherwise: Joi.allow(),
  }).messages({ 'number.greater': '"To" must be more than "From"' }),
  balanceFrom: Joi.any().label('From').when('isBalanceRange', { is: true, then: Joi.number().required().min(0).max(999), otherwise: Joi.allow() }),
  balanceTo: Joi.any().label('To').when('isBalanceRange', {
    is: true, then: Joi.number().required().min(1).max(999).greater(Joi.ref('balanceFrom')), otherwise: Joi.allow(),
  }).messages({ 'number.greater': '"To" must be more than "From"' }),
  activity: Joi.any().label('Days').when('isLastActive', { is: true, then: Joi.number().required().min(1).max(999), otherwise: Joi.allow() }),
})

/** what a campaign can sell: on sale, paid, for everyone (not one client's custom / a demo), with a PayPro product */
const isBuyable = (p: Package) =>
  p.isActive && !p.isFree && !p.uid && !!p.productId && !!p.price && !['custom', 'demo', 'webinthub_demo'].includes(p.type)
const hasAnnual = (p?: Package | null) => !!p?.annualProductId && !!p?.annualPrice

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

// `group`: buttons inside (chips, the paragraph editor) — a <label> would click the first one
function Field({ label, error, count, max, group, children }: {
  label: string; error?: string; count?: number; max?: number; group?: boolean; children: React.ReactNode
}) {
  const Tag = group ? 'div' : 'label'
  return (
    <Tag className={s.field}>
      <span className={s.labelRow}>
        <span className={s.label}>{label}</span>
        {max != null && <span className={`${s.count} ${(count || 0) > max ? s.over : ''}`}>{count || 0} / {max}</span>}
      </span>
      {children}
      {error && <span className={s.err}>{error}</span>}
    </Tag>
  )
}
function Section({ n, title, sub, children }: { n: number; title: string; sub: string; children: React.ReactNode }) {
  return (
    <section className={s.section}>
      <div className={s.sectionHead}><span className={s.num}>{n}</span><b>{title}</b><span>· {sub}</span></div>
      <div className={s.sectionBody}>{children}</div>
    </section>
  )
}
function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" className={`${s.chip} ${on ? s.chipOn : ''}`} onClick={onClick}>{children}</button>
}
function Toggle({ on, onChange, disabled, label }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" className={`${s.toggle} ${on ? s.toggleOn : ''}`} disabled={disabled} onClick={() => onChange(!on)}>
      <span className={s.switch}><span /></span>{label}
    </button>
  )
}

function CopyBtn({ text }: { text: string }) {
  const [done, setDone] = React.useState(false)
  return (
    <button
      type="button"
      className={s.copyBtn}
      onClick={() => { void navigator.clipboard?.writeText(text).catch(() => undefined); setDone(true); window.setTimeout(() => setDone(false), 1200) }}
    >
      <ContentCopy sx={{ fontSize: 13 }} />{done ? 'Copied' : 'Copy'}
    </button>
  )
}

/** Test of a "buy a package" button: what is saved, the checkout the user gets, and that checkout to look at */
function BuyTest({ pkg, billing, saved }: { pkg: Package; billing: Billing; saved: string }) {
  const forUser = checkoutUrl(pkg, billing === 'annual', '<the user\'s uid>')
  const toLook = checkoutUrl(pkg, billing === 'annual', null)
  return (
    <div className={s.testPanel}>
      <div className={s.testStep}>
        <span className={s.testHead}><b>1. Saved in the campaign</b><CopyBtn text={saved} /></span>
        <code className={s.code}>{saved}</code>
      </div>
      <div className={s.testStep}>
        <span className={s.testHead}><b>2. Opened for the user on click</b><CopyBtn text={forUser} /></span>
        <pre className={s.code}>{readableUrl(forUser)}</pre>
        <span className={s.hint}>
          The dashboard fills in the signed-in user&apos;s uid and its own currency setting, and takes the product and price from the
          package at the moment of the click. A downgrade or a package the user cannot buy opens the Packages page instead.
        </span>
      </div>
      <div className={s.testStep}>
        <button type="button" className={s.testOpen} onClick={() => window.open(toLook, '_blank', 'noopener,noreferrer')}>
          <OpenInNew sx={{ fontSize: 15 }} />Open this checkout in PayPro
        </button>
        <span className={s.hint}>To check the product, name and price. It is linked to no account — do not pay.</span>
      </div>
    </div>
  )
}

export function CampaignDialogV2({ mode, data, theme, tableRef, onClose }: {
  mode: 'create' | 'edit'
  data?: ICampaign
  theme: 'light' | 'dark'
  tableRef: React.RefObject<TableRef>
  onClose: () => void
}) {
  const dispatch = useDispatch()
  const { packageRules } = useSelector((state) => state.package)
  React.useEffect(() => { dispatch(getPackageRules) }, [dispatch])

  const [name, setName] = React.useState(data?.name || '')
  const [title, setTitle] = React.useState(data?.title || '')
  // the video lives in the stored paragraph's first line ({video:youtube:…}); here it is a field of its own
  const initialSplit = splitVideo(data?.paragraph || '')
  const [paragraph, setParagraph] = React.useState(initialSplit.text)
  const [videoInput, setVideoInput] = React.useState(initialSplit.video ? `https://youtu.be/${initialSplit.video}` : '')
  const videoId = youtubeId(videoInput)
  const [actionName, setActionName] = React.useState(data?.actionName || '')
  const initialBuy = parseBuyAction(data?.actionUrl)
  const [actionKind, setActionKind] = React.useState<'link' | 'package'>(initialBuy ? 'package' : 'link')
  const [actionUrl, setActionUrl] = React.useState(initialBuy ? '' : data?.actionUrl || '')
  const [buyId, setBuyId] = React.useState<number | null>(initialBuy?.packageId ?? null)
  const [billing, setBilling] = React.useState<Billing>(initialBuy?.billing || 'monthly')
  // the whole catalogue, for prices and annual plans — the marketing list has titles only
  const [catalogue, setCatalogue] = React.useState<Package[] | null>(null)
  const [testOpen, setTestOpen] = React.useState(false)
  React.useEffect(() => {
    let alive = true
    api({ method: 'GET', url: '/package/admin', params: { offset: 0, limit: 1000 } })
      .then(({ data: res }) => { if (alive) setCatalogue(Array.isArray(res) ? res : res?.list || []) })
      .catch(() => { if (alive) setCatalogue([]) })
    return () => { alive = false }
  }, [])
  const [type, setType] = React.useState<string>(data?.type || 'bottom-right')
  const [frequency, setFrequency] = React.useState<string>(data?.frequency || 'once-a-day')
  const [durationHour, setDurationHour] = React.useState(str(data?.duration?.hours ?? 24))
  const [durationMinute, setDurationMinute] = React.useState(str(data?.duration?.minutes ?? 0))
  const [showCountdown, setShowCountdown] = React.useState(!!data?.showCountdown)
  const [subscribers, setSubscribers] = React.useState<SubscriberType[]>((data?.subscribers || []).filter((x) => x !== 'all'))
  const [pages, setPages] = React.useState<string[]>((data?.pages || []).filter((x) => x !== 'all') as string[])
  const [packages, setPackages] = React.useState<string[]>(data?.packages || [])
  const [isLtv, setIsLtv] = React.useState(!!data?.ltvRange)
  const [isBalance, setIsBalance] = React.useState(!!data?.balanceRange)
  const [isLastActive, setIsLastActive] = React.useState(data?.lastActive != null)
  const [ltvFrom, setLtvFrom] = React.useState(str(data?.ltvRange?.from))
  const [ltvTo, setLtvTo] = React.useState(str(data?.ltvRange?.to))
  const [balanceFrom, setBalanceFrom] = React.useState(str(data?.balanceRange?.from))
  const [balanceTo, setBalanceTo] = React.useState(str(data?.balanceRange?.to))
  const [activity, setActivity] = React.useState(str(data?.lastActive))
  const [errors, setErrors] = React.useState<ErrorsState>(null)
  const [device, setDevice] = React.useState<'desktop' | 'phone'>('desktop')
  // the user's theme in the preview — starts as the admin's own
  const [previewMode, setPreviewMode] = React.useState<'light' | 'dark'>(theme)
  const [askRestart, setAskRestart] = React.useState(false)

  React.useEffect(() => { setErrors(null); dispatch(clearFieldError) }, [dispatch, name, title, paragraph, videoInput, actionName, actionKind, actionUrl, buyId, billing, type, frequency,
    durationHour, durationMinute, showCountdown, subscribers, pages, packages, isLtv, isBalance, isLastActive, ltvFrom, ltvTo, balanceFrom, balanceTo, activity])

  const rules = packageRules || []
  const isFreeSelected = rules.some((r) => r.isFree && packages.includes(r.title))
  const isOnlyFree = isFreeSelected && packages.length === 1
  const isFreeAndPaid = isFreeSelected && packages.length > 1

  // the classic form's consequences of picking the free package
  React.useEffect(() => {
    if (isOnlyFree) setSubscribers([])
    if (isFreeSelected) setIsLtv(false)
  }, [isOnlyFree, isFreeSelected])

  const toggleIn = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])
  const allTiers = TIERS.every((x) => subscribers.includes(x.key))
  const allPages = PAGES.every((x) => pages.includes(x.key))
  const packageOptions = rules.map((r) => ({ key: r.title, title: r.title, free: r.isFree }))
  const allPackages = rules.length > 0 && rules.every((r) => packages.includes(r.title))

  const buyable = (catalogue || []).filter((p) => isBuyable(p) || p.id === buyId)
  const buyPkg = (catalogue || []).find((p) => p.id === buyId) || null
  const finalUrl = actionKind === 'package'
    ? (buyId ? buyActionUrl({ packageId: buyId, billing, webint: (buyPkg?.type || '').startsWith('webinthub') }) : '')
    : actionUrl.trim()

  function pickPackage(id: number | null) {
    setBuyId(id)
    const p = (catalogue || []).find((x) => x.id === id)
    if (!hasAnnual(p)) setBilling('monthly')
  }

  const stats = paragraphStats(paragraph)
  const validationData = {
    name: name.trim(), title: title.trim(), paragraph: joinVideo(videoId, paragraph.trim()), actionName: actionName.trim(),
    videoOk: !videoInput.trim() || !!videoId,
    actionKind, actionUrl: finalUrl, buyPackage: actionKind === 'package' ? buyId ?? undefined : undefined,
    paragraphText: paragraphText(paragraph).trim(), paragraphLinks: stats.links, paragraphCodes: stats.codes, paragraphListItems: stats.listItems,
    type, durationHour, durationMinute, showCountdown, frequency, subscribers, pages, packages,
    ltvFrom, ltvTo, balanceFrom, balanceTo, activity,
    isOnlyFreePackageSelected: isOnlyFree, isLtv, isBalanceRange: isBalance, isLastActive,
  }

  function validate(): boolean {
    const error = validateForm(schema, validationData as unknown as FormState)
    setErrors(error || null)
    return !error
  }

  function save() {
    if (!validate()) return
    const body: FormState = {
      name: validationData.name, title: validationData.title, paragraph: validationData.paragraph,
      actionName: validationData.actionName, actionUrl: finalUrl,
      type, frequency, subscribers, showCountdown, packages, pages,
      ltvRange: isLtv ? { from: Number(ltvFrom), to: Number(ltvTo) } : null,
      balanceRange: isBalance ? { from: Number(balanceFrom), to: Number(balanceTo) } : null,
      lastActive: isLastActive ? Number(activity) : null,
      duration: { hours: Number(durationHour), minutes: Number(durationMinute) },
    }
    const done = () => tableRef.current?.refresh()
    if (mode === 'create') dispatch(postCampaign(body, done))
    else dispatch(putCampaign(data?.id as number, body, done))
    onClose()
  }

  // the classic edit form's "activate": runs the saved campaign again from now
  function restart() {
    if (!validate()) return
    dispatch(activateCampaign(data?.id as number, () => tableRef.current?.refresh()))
    onClose()
  }

  const err = (k: string) => (errors as any)?.[k] as string | undefined
  const pageNames = allPages ? 'all pages' : PAGES.filter((p) => pages.includes(p.key)).map((p) => p.label).join(', ') || '—'
  const audience = [
    isOnlyFree ? null : allTiers ? 'all subscribers' : TIERS.filter((x) => subscribers.includes(x.key)).map((x) => x.label).join(', ') || '—',
    packages.length ? (allPackages ? 'all packages' : packages.join(', ')) : null,
    isLtv && ltvFrom && ltvTo ? `LTV €${ltvFrom}–${ltvTo}` : null,
    isBalance && balanceFrom && balanceTo ? `balance €${balanceFrom}–${balanceTo}` : null,
    isLastActive && activity ? `inactive ${activity}+ days` : null,
  ].filter(Boolean).join(' · ')
  const hours = Number(durationHour) || 0
  const minutes = Number(durationMinute) || 0

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.icon}><CampaignIcon sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}>
            <b>{mode === 'create' ? 'New pop-up campaign' : `Edit pop-up campaign: ${data?.name || ''}`}</b>
            <span>The pop-up on the right is what matching users will see.</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.main}>
          <div className={s.form}>
            <Section n={1} title="Content" sub="what the pop-up says">
              <Field label="Campaign name — also on the banner" error={err('name')} count={name.length} max={NAME_MAX}>
                <input className={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="U.S. KYC Reports with TellData" />
              </Field>
              <Field label="Main title" error={err('title')} count={title.length} max={TITLE_MAX}>
                <input className={s.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="The blue line under the banner" />
              </Field>
              <Field label="Video — YouTube, optional" error={err('videoOk')}>
                <input className={s.input} value={videoInput} onChange={(e) => setVideoInput(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" />
              </Field>
              {videoId && (
                <div className={s.video}>
                  <span className={s.videoThumb} style={{ backgroundImage: `url(${youtubeThumb(videoId)})` }}><PlayArrowRounded sx={{ fontSize: 22 }} /></span>
                  <span className={s.videoText}><b>Above the text, 16:9</b>Starts by itself without sound; the user can turn the sound on in the player.</span>
                  <button type="button" className={s.videoRemove} onClick={() => setVideoInput('')} aria-label="Remove the video" title="Remove the video"><CloseIcon sx={{ fontSize: 16 }} /></button>
                </div>
              )}
              <Field label="Paragraph" group error={err('paragraphText') || err('paragraph') || err('paragraphLinks') || err('paragraphCodes') || err('paragraphListItems')} count={paragraphText(paragraph).length} max={PARAGRAPH_MAX}>
                <ParagraphEditor value={paragraph} onChange={setParagraph} placeholder="A few lines under the title — bold, lists, links, a promo code…" />
              </Field>
              <Field label="The button" group>
                <div className={s.chips}>
                  <Chip on={actionKind === 'link'} onClick={() => setActionKind('link')}>Opens a link</Chip>
                  <Chip on={actionKind === 'package'} onClick={() => setActionKind('package')}>Buys a package</Chip>
                </div>
              </Field>
              <div className={s.grid2}>
                <Field label="Button text" error={err('actionName')} count={actionName.length} max={ACTION_MAX}>
                  <input className={s.input} value={actionName} onChange={(e) => setActionName(e.target.value)} placeholder={actionKind === 'package' ? 'Buy now' : 'Try it now'} />
                </Field>
                {actionKind === 'link' ? (
                  <Field label="Button link" error={err('actionUrl')}>
                    <span className={s.linkRow}>
                      <input className={s.input} value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="https://…" />
                      <button
                        type="button"
                        className={s.testBtn}
                        disabled={!/^https?:\/\/\S+\.\S+/i.test(actionUrl.trim())}
                        onClick={(e) => { e.preventDefault(); window.open(actionUrl.trim(), '_blank', 'noopener,noreferrer') }}
                        title="Open the link in a new tab"
                      >
                        <OpenInNew sx={{ fontSize: 15 }} />Test
                      </button>
                    </span>
                  </Field>
                ) : (
                  <Field label="Package" error={err('buyPackage')}>
                    <select className={s.input} value={buyId ?? ''} onChange={(e) => pickPackage(e.target.value ? Number(e.target.value) : null)} disabled={!catalogue}>
                      <option value="">{catalogue ? 'Choose a package…' : 'Loading packages…'}</option>
                      {buyable.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title} — €{p.price}/mo{hasAnnual(p) ? ` · €${p.annualPrice}/yr` : ''}{isBuyable(p) ? '' : ' (not on sale)'}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
              {actionKind === 'package' && (
                <div className={s.buyRow}>
                  <div className={s.chips}>
                    <Chip on={billing === 'monthly'} onClick={() => setBilling('monthly')}>Monthly{buyPkg?.price ? ` · €${buyPkg.price}` : ''}</Chip>
                    {hasAnnual(buyPkg) && <Chip on={billing === 'annual'} onClick={() => setBilling('annual')}>Annual · €{buyPkg?.annualPrice}</Chip>}
                    <button type="button" className={`${s.testBtn} ${testOpen ? s.testBtnOn : ''}`} disabled={!buyPkg} onClick={() => setTestOpen(!testOpen)}>
                      <ScienceOutlined sx={{ fontSize: 15 }} />Test
                    </button>
                  </div>
                  {testOpen && buyPkg && <BuyTest pkg={buyPkg} billing={billing} saved={finalUrl} />}
                  {buyPkg && !isBuyable(buyPkg) && <div className={s.warn}>This package is not on sale any more (inactive, free or without a PayPro product) — choose another.</div>}
                  <span className={s.hint}>
                    The button starts the checkout for this package on the user&apos;s own account, at its current price.
                    If the user cannot buy it there (e.g. a downgrade), the Packages page opens instead.
                  </span>
                </div>
              )}
            </Section>

            <Section n={2} title="Display" sub="how and how long">
              <div className={s.types}>
                {TYPES.map((x) => (
                  <button key={x.key} type="button" className={`${s.typeCard} ${type === x.key ? s.typeOn : ''}`} onClick={() => setType(x.key)}>
                    <span className={`${s.mini} ${x.key === 'center-overlay' ? s.miniCenter : ''}`}><i /></span>
                    {x.label}
                  </button>
                ))}
              </div>
              {err('type') && <span className={s.err}>{err('type')}</span>}
              <Field label="Show" group error={err('frequency')}>
                <div className={s.chips}>{FREQUENCIES.map((x) => <Chip key={x.key} on={frequency === x.key} onClick={() => setFrequency(x.key)}>{x.label}</Chip>)}</div>
              </Field>
              <div className={s.grid3}>
                <Field label="Runs for — hours" error={err('durationHour')}>
                  <input className={s.input} type="number" min={0} max={999} value={durationHour} onChange={(e) => setDurationHour(e.target.value)} />
                </Field>
                <Field label="minutes" error={err('durationMinute')}>
                  <input className={s.input} type="number" min={0} max={59} value={durationMinute} onChange={(e) => setDurationMinute(e.target.value)} />
                </Field>
                <div className={s.toggleCell}><Toggle on={showCountdown} onChange={setShowCountdown} label="Show countdown" /></div>
              </div>
            </Section>

            <Section n={3} title="Where" sub="pages">
              <div className={s.chips}>
                <Chip on={allPages} onClick={() => setPages(allPages ? [] : PAGES.map((p) => p.key))}>All pages</Chip>
                {PAGES.map((p) => <Chip key={p.key} on={pages.includes(p.key)} onClick={() => setPages(toggleIn(pages, p.key))}>{p.label}</Chip>)}
              </div>
              {err('pages') && <span className={s.err}>{err('pages')}</span>}
            </Section>

            <Section n={4} title="Who" sub="audience">
              <Field label="Packages" group error={err('packages')}>
                <PackagePicker rules={packageOptions} value={packages} onChange={setPackages} loading={!packageRules} />
              </Field>
              {isOnlyFree && <div className={s.warn}>{t('marketing.warning.isOnlyFreePackageSelected')}</div>}
              {isFreeAndPaid && <div className={s.warn}>{t('marketing.warning.isFreeAndPaidPackageSelected')}</div>}
              {!isOnlyFree && (
                <Field label="Subscribers" group error={err('subscribers')}>
                  <div className={s.chips}>
                    <Chip on={allTiers} onClick={() => setSubscribers(allTiers ? [] : TIERS.map((x) => x.key))}>All</Chip>
                    {TIERS.map((x) => <Chip key={x.key} on={subscribers.includes(x.key)} onClick={() => setSubscribers(toggleIn(subscribers, x.key))}>{x.label}</Chip>)}
                  </div>
                </Field>
              )}
              <div className={s.criteria}>
                <div className={s.criterion}>
                  <Toggle on={isLtv} onChange={setIsLtv} disabled={isFreeSelected} label="LTV range, €" />
                  {isLtv && (
                    <div className={s.range}>
                      <input className={s.input} type="number" placeholder="from" value={ltvFrom} onChange={(e) => setLtvFrom(e.target.value)} />
                      <span>–</span>
                      <input className={s.input} type="number" placeholder="to" value={ltvTo} onChange={(e) => setLtvTo(e.target.value)} />
                    </div>
                  )}
                  {(err('ltvFrom') || err('ltvTo')) && <span className={s.err}>{err('ltvFrom') || err('ltvTo')}</span>}
                </div>
                <div className={s.criterion}>
                  <Toggle on={isBalance} onChange={setIsBalance} label="Balance range, €" />
                  {isBalance && (
                    <div className={s.range}>
                      <input className={s.input} type="number" placeholder="from" value={balanceFrom} onChange={(e) => setBalanceFrom(e.target.value)} />
                      <span>–</span>
                      <input className={s.input} type="number" placeholder="to" value={balanceTo} onChange={(e) => setBalanceTo(e.target.value)} />
                    </div>
                  )}
                  {(err('balanceFrom') || err('balanceTo')) && <span className={s.err}>{err('balanceFrom') || err('balanceTo')}</span>}
                </div>
                <div className={s.criterion}>
                  <Toggle on={isLastActive} onChange={setIsLastActive} label="Inactive for, days" />
                  {isLastActive && (
                    <div className={s.range}>
                      <input className={s.input} type="number" min={1} max={999} placeholder="14" value={activity} onChange={(e) => setActivity(e.target.value)} />
                      <span>+ days</span>
                    </div>
                  )}
                  {err('activity') && <span className={s.err}>{err('activity')}</span>}
                </div>
              </div>
            </Section>
          </div>

          <aside className={s.preview}>
            <div className={s.previewHead}>
              <span>Live preview</span>
              <div className={s.segs}>
                <div className={s.seg}>
                  <button type="button" className={device === 'desktop' ? s.segOn : ''} onClick={() => setDevice('desktop')}>Desktop</button>
                  <button type="button" className={device === 'phone' ? s.segOn : ''} onClick={() => setDevice('phone')}>Phone</button>
                </div>
                <div className={s.seg}>
                  <button type="button" className={previewMode === 'light' ? s.segOn : ''} onClick={() => setPreviewMode('light')}>Light</button>
                  <button type="button" className={previewMode === 'dark' ? s.segOn : ''} onClick={() => setPreviewMode('dark')}>Dark</button>
                </div>
              </div>
            </div>
            <div className={s.screen}>
              <CampaignScreen
                mode={previewMode}
                device={device}
                data={{ name, title, paragraph: joinVideo(videoId, paragraph), actionName, actionUrl, type, showCountdown, hours, minutes }}
                size={{ width: 500, height: 480, phoneWidth: 300, phoneHeight: 540 }}
              />
            </div>
            <div className={s.summary}>
              Shown on <b>{pageNames}</b> · <b>{FREQUENCIES.find((f) => f.key === frequency)?.label.toLowerCase() || '—'}</b>
              {' '}· to <b>{audience || '—'}</b> · for <b>{hours}h{minutes ? ` ${minutes}m` : ''}</b>
              {actionKind === 'package' && buyPkg && <> · the button buys <b>{buyPkg.title}, {billing}</b></>}
            </div>
          </aside>
        </div>

        <div className={s.foot}>
          {errors ? <span className={s.footErr}>Some fields need attention</span> : (
            <span className={s.footNote}>{mode === 'create' ? 'Goes live as soon as it is created' : 'Saving keeps the running time; Restart runs it again from now'}</span>
          )}
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          {mode === 'edit' && <button type="button" className={s.ghost} onClick={() => setAskRestart(true)}>Restart</button>}
          <button type="button" className={s.primary} onClick={save}>{mode === 'create' ? 'Create & launch' : 'Save changes'}</button>
        </div>

        {askRestart && (
          <div className={s.confirm}>
            <div className={s.confirmBox}>
              <b>Restart “{data?.name}”?</b>
              <span>It runs again for its saved duration from now, and matching users see it again. Unsaved edits here are not applied — save them first.</span>
              <div className={s.confirmBtns}>
                <button type="button" className={s.ghost} onClick={() => setAskRestart(false)}>Back</button>
                <button type="button" className={s.primary} onClick={() => { setAskRestart(false); restart() }}>Restart</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
